import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Archive, ArchiveRestore, Trash2, ChevronDown, FolderOpen } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "../components/ui/alert-dialog";
import ScreenHeader from "../components/layout/ScreenHeader";
import ScopeToggle from "../components/ScopeToggle";
import EmptyState from "../components/EmptyState";
import { api, formatApiErrorDetail } from "../lib/api";
import { useScope } from "../context/ScopeContext";
import { toast } from "sonner";

export default function Categorias() {
  const { scope } = useScope();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);

  // Editor modal
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState("create"); // "create" | "edit"
  const [editorTarget, setEditorTarget] = useState(null);
  const [editorName, setEditorName] = useState("");
  const [editorKind, setEditorKind] = useState("gasto");
  const [editorSaving, setEditorSaving] = useState(false);

  // Confirm dialog
  const [confirm, setConfirm] = useState(null); // { action: "archive"|"unarchive"|"delete", cat: {...} }
  const [confirmRunning, setConfirmRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/categories", {
        params: { scope, include_archived: true },
      });
      setCategories(data);
    } catch (_) {
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  const { activeIncome, activeExpense, archived } = useMemo(() => {
    const a = { activeIncome: [], activeExpense: [], archived: [] };
    categories.forEach((c) => {
      if (c.archived) a.archived.push(c);
      else if (c.kind === "ingreso") a.activeIncome.push(c);
      else a.activeExpense.push(c);
    });
    return a;
  }, [categories]);

  const openCreate = (kind) => {
    setEditorMode("create");
    setEditorTarget(null);
    setEditorName("");
    setEditorKind(kind);
    setEditorOpen(true);
  };

  const openEdit = (cat) => {
    setEditorMode("edit");
    setEditorTarget(cat);
    setEditorName(cat.name);
    setEditorKind(cat.kind);
    setEditorOpen(true);
  };

  const handleEditorSubmit = async (e) => {
    e.preventDefault();
    const name = editorName.trim();
    if (!name) {
      toast.error("Ponle un nombre a la categoría");
      return;
    }
    setEditorSaving(true);
    try {
      if (editorMode === "create") {
        await api.post("/categories", { name, kind: editorKind }, { params: { scope } });
        toast.success("Categoría creada");
      } else {
        await api.patch(`/categories/${editorTarget.id}`, { name });
        toast.success("Categoría actualizada");
      }
      setEditorOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al guardar");
    } finally {
      setEditorSaving(false);
    }
  };

  const runConfirm = async () => {
    if (!confirm) return;
    setConfirmRunning(true);
    try {
      const id = confirm.cat.id;
      if (confirm.action === "archive") {
        await api.post(`/categories/${id}/archive`);
        toast.success("Categoría archivada");
      } else if (confirm.action === "unarchive") {
        await api.post(`/categories/${id}/unarchive`);
        toast.success("Categoría reactivada");
      } else if (confirm.action === "delete") {
        await api.delete(`/categories/${id}`);
        toast.success("Categoría eliminada");
      }
      setConfirm(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Operación no permitida");
      setConfirm(null);
    } finally {
      setConfirmRunning(false);
    }
  };

  return (
    <div data-testid="categorias-screen">
      <ScreenHeader
        eyebrow={scope === "shared" ? "Espacio compartido" : "Tu espacio"}
        title="Categorías"
        subtitle="Personaliza cómo organizas tus ingresos y gastos. Los movimientos antiguos conservan su categoría original."
      />

      <div className="px-6 mb-4">
        <ScopeToggle />
      </div>

      {loading ? (
        <div className="px-6"><div className="rounded-3xl bg-white border border-line p-6 animate-pulse h-32" /></div>
      ) : (
        <>
          <Section
            title="Gastos"
            kind="gasto"
            items={activeExpense}
            onCreate={() => openCreate("gasto")}
            onEdit={openEdit}
            onArchive={(c) => setConfirm({ action: "archive", cat: c })}
            onDelete={(c) => setConfirm({ action: "delete", cat: c })}
          />

          <Section
            title="Ingresos"
            kind="ingreso"
            items={activeIncome}
            onCreate={() => openCreate("ingreso")}
            onEdit={openEdit}
            onArchive={(c) => setConfirm({ action: "archive", cat: c })}
            onDelete={(c) => setConfirm({ action: "delete", cat: c })}
          />

          {archived.length > 0 && (
            <div className="px-6 mt-4" data-testid="archived-section">
              <button
                type="button"
                onClick={() => setShowArchived((v) => !v)}
                className="w-full flex items-center justify-between bg-white border border-line/70 rounded-2xl px-5 py-3.5"
                data-testid="archived-toggle"
              >
                <div className="flex items-center gap-2.5">
                  <div className="h-8 w-8 rounded-xl bg-bone text-soft flex items-center justify-center">
                    <Archive size={14} />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-bold text-ink">Archivadas</p>
                    <p className="text-[11px] text-soft">
                      {archived.length} · conservan historial, no aparecen en captura
                    </p>
                  </div>
                </div>
                <ChevronDown
                  size={16}
                  className={`text-soft transition-transform ${showArchived ? "rotate-180" : ""}`}
                />
              </button>

              {showArchived && (
                <div className="mt-2 space-y-1.5" data-testid="archived-list">
                  {archived.map((c) => (
                    <CategoryRow
                      key={c.id}
                      cat={c}
                      muted
                      onEdit={openEdit}
                      onUnarchive={(c) => setConfirm({ action: "unarchive", cat: c })}
                      onDelete={(c) => setConfirm({ action: "delete", cat: c })}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Editor */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-sm bg-bone border-line rounded-3xl" data-testid="category-editor-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editorMode === "create" ? "Nueva categoría" : "Renombrar categoría"}
            </DialogTitle>
            <DialogDescription className="text-soft text-sm">
              {editorMode === "create"
                ? "Aparecerá en tus selectores al capturar movimientos."
                : "Los movimientos pasados conservan el nombre con el que fueron capturados (snapshot)."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditorSubmit} className="space-y-4 mt-2">
            {editorMode === "create" && (
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.16em] text-sage-700 block mb-2">
                  Tipo
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setEditorKind("gasto")}
                    data-testid="category-editor-kind-gasto"
                    className={`py-2.5 rounded-xl text-sm font-bold border transition-colors ${
                      editorKind === "gasto"
                        ? "bg-sage-900 text-bone border-sage-900"
                        : "bg-white border-line text-ink"
                    }`}
                  >
                    Gasto
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditorKind("ingreso")}
                    data-testid="category-editor-kind-ingreso"
                    className={`py-2.5 rounded-xl text-sm font-bold border transition-colors ${
                      editorKind === "ingreso"
                        ? "bg-sage-900 text-bone border-sage-900"
                        : "bg-white border-line text-ink"
                    }`}
                  >
                    Ingreso
                  </button>
                </div>
              </div>
            )}
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.16em] text-sage-700 block mb-1">
                Nombre
              </label>
              <input
                data-testid="category-editor-name"
                value={editorName}
                onChange={(e) => setEditorName(e.target.value)}
                placeholder="Comida, Renta, Salario…"
                maxLength={40}
                autoFocus
                className="w-full bg-white border border-line rounded-xl px-3 py-3 outline-none focus:border-sage-500"
              />
            </div>
            <button
              type="submit"
              disabled={editorSaving}
              data-testid="category-editor-save"
              className="w-full bg-sage-900 text-bone rounded-2xl py-3 font-bold hover:bg-sage-900/90 disabled:opacity-60"
            >
              {editorSaving
                ? "Guardando…"
                : editorMode === "create" ? "Crear categoría" : "Guardar cambios"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirm action */}
      <AlertDialog
        open={confirm !== null}
        onOpenChange={(o) => { if (!o && !confirmRunning) setConfirm(null); }}
      >
        <AlertDialogContent
          className="max-w-sm bg-bone border-line rounded-3xl"
          data-testid="confirm-category-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">
              {confirm?.action === "delete" && `¿Eliminar "${confirm.cat.name}"?`}
              {confirm?.action === "archive" && `¿Archivar "${confirm.cat.name}"?`}
              {confirm?.action === "unarchive" && `¿Reactivar "${confirm.cat.name}"?`}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-soft text-sm leading-relaxed">
              {confirm?.action === "delete" &&
                "Solo se puede eliminar si ningún movimiento usa esta categoría. Si tiene historial, archívala para conservarlo."}
              {confirm?.action === "archive" &&
                "Dejará de aparecer en selectores de nuevos movimientos, pero los movimientos pasados conservan su nombre."}
              {confirm?.action === "unarchive" &&
                "Volverá a estar disponible en los selectores de captura."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2 mt-2">
            <AlertDialogCancel
              disabled={confirmRunning}
              data-testid="confirm-category-cancel"
              className="bg-white border-line text-ink rounded-2xl font-bold"
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); runConfirm(); }}
              disabled={confirmRunning}
              data-testid="confirm-category-confirm"
              className={`rounded-2xl font-bold ${
                confirm?.action === "delete"
                  ? "bg-clay text-bone hover:bg-clay/90"
                  : "bg-sage-900 text-bone hover:bg-sage-900/90"
              }`}
            >
              {confirmRunning
                ? "Procesando…"
                : confirm?.action === "delete" ? "Sí, eliminar"
                : confirm?.action === "archive" ? "Sí, archivar"
                : "Sí, reactivar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Section({ title, kind, items, onCreate, onEdit, onArchive, onDelete }) {
  return (
    <section className="px-6 mt-2" data-testid={`section-${kind}`}>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-bold uppercase tracking-[0.18em] text-sage-700">
          {title}
        </h2>
        <button
          type="button"
          onClick={onCreate}
          data-testid={`section-${kind}-create`}
          className="h-9 px-3 rounded-full bg-sage-900 text-bone text-xs font-bold inline-flex items-center gap-1.5 hover:bg-sage-900/90 active:scale-95 transition-all"
        >
          <Plus size={12} strokeWidth={2.6} /> Nueva
        </button>
      </div>
      {items.length === 0 ? (
        <EmptyState
          icon={FolderOpen}
          title={`Sin ${title.toLowerCase()} aún`}
          description="Crea tu primera categoría para empezar a organizar."
          testid={`empty-${kind}`}
        />
      ) : (
        <div className="space-y-1.5 mb-6">
          {items.map((c) => (
            <CategoryRow
              key={c.id}
              cat={c}
              onEdit={onEdit}
              onArchive={onArchive}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function CategoryRow({ cat, muted, onEdit, onArchive, onUnarchive, onDelete }) {
  const used = cat.usage_count > 0;
  return (
    <div
      className={`rounded-2xl border px-4 py-3 flex items-center gap-3 ${
        muted ? "bg-bone/50 border-line/60" : "bg-white border-line/70"
      }`}
      data-testid={`category-row-${cat.id}`}
    >
      <div className="flex-1 min-w-0">
        <p className={`font-semibold truncate ${muted ? "text-soft" : "text-ink"}`}>
          {cat.name}
        </p>
        <p className="text-[11px] text-soft">
          {cat.usage_count > 0
            ? `${cat.usage_count} movimiento${cat.usage_count === 1 ? "" : "s"}`
            : "Sin uso"}
          {muted && " · archivada"}
        </p>
      </div>
      <div className="flex items-center gap-1">
        {!muted && (
          <IconButton
            onClick={() => onEdit(cat)}
            label="Renombrar"
            testid={`category-edit-${cat.id}`}
            icon={Pencil}
          />
        )}
        {muted ? (
          <IconButton
            onClick={() => onUnarchive(cat)}
            label="Reactivar"
            testid={`category-unarchive-${cat.id}`}
            icon={ArchiveRestore}
          />
        ) : (
          <IconButton
            onClick={() => onArchive(cat)}
            label="Archivar"
            testid={`category-archive-${cat.id}`}
            icon={Archive}
          />
        )}
        <IconButton
          onClick={() => onDelete(cat)}
          label={used ? "No se puede eliminar (tiene uso)" : "Eliminar"}
          testid={`category-delete-${cat.id}`}
          icon={Trash2}
          danger
          disabled={used}
        />
      </div>
    </div>
  );
}

function IconButton({ onClick, label, testid, icon: Icon, danger, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-label={label}
      title={label}
      disabled={disabled}
      className={`h-9 w-9 rounded-xl flex items-center justify-center transition-colors ${
        disabled
          ? "text-line cursor-not-allowed"
          : danger
          ? "text-clay hover:bg-clay/10"
          : "text-soft hover:bg-sage-50 hover:text-sage-700"
      }`}
    >
      <Icon size={15} strokeWidth={1.9} />
    </button>
  );
}
